import { profile } from "console";
import { API, API_BASEURL, buildUrl, SWR_CONFIG } from "../../config";
import useGetSWR from "../../hooks/useGetSWR";
import { IsimplifiedUser } from "../../types";
import { CustomSelect } from "./genericDropdowns/CustomSelect";

type InputProps = {
    title?: string;
    placeholder?: string;
    name?: string;
    onChangeFunction?: (value: string, label: string) => void;
    className?: string;
    inputClassName?: string;
    titleClassname?: string;
    outerClass?: string;
    editing?: boolean;
    authO?: boolean;
    textBoxClass?: string;
    value?: string | number;
    children?: JSX.Element | false | undefined;
    authCheck?: boolean;
    calendar?: boolean;
    assignable?: boolean;
    task?: boolean;
    selectStyles?: any;
    payment?: boolean;
    disabledOptions?: string[]
    menuPlacement?: 'auto' | 'bottom' | 'top';
    addRoundRobin?: boolean;
    isClearable?: boolean;
    roles?: string[];
    isLoading?: boolean;
    showUserImage?: boolean;
    stateFilter?: string;
};

export const DropDownUsers = ({
    title,
    placeholder = "",
    name = "",
    onChangeFunction,
    className,
    inputClassName,
    titleClassname,
    editing,
    textBoxClass,
    selectStyles,
    value = "",
    authO,
    children,
    authCheck,
    calendar = false,
    assignable = false,
    task = false,
    payment = false,
    outerClass,
    disabledOptions,
    menuPlacement,
    addRoundRobin,
    isClearable,
    roles,
    isLoading,
    showUserImage,
    stateFilter,
}: InputProps) => {
    const url = !calendar
        ? buildUrl(API.USER_SIMPLE_ALL_DROPDOWN, {}, API_BASEURL, {
            authCheck: authCheck,
            assignable: assignable,
            payment,
            roles: roles ? roles.join('%') : "",
            state: stateFilter || "",
        })
        : buildUrl(API.USER_SIMPLE_CALENDAR, {}, API_BASEURL, { task });
    const { data: userListRes } = useGetSWR<IsimplifiedUser>(url, SWR_CONFIG.FORMS);

    // When a state filter is active, the selected value may belong to a user who was assigned
    // before this feature existed (no states_licensed_in set) and won't appear in the filtered
    // list. Fetch without the state filter so we can still resolve the display name.
    const urlNoStateFilter = stateFilter && !calendar
        ? buildUrl(API.USER_SIMPLE_ALL_DROPDOWN, {}, API_BASEURL, {
            authCheck: authCheck,
            assignable: assignable,
            payment,
            roles: roles ? roles.join('%') : "",
        })
        : null;
    const { data: userListResNoFilter } = useGetSWR<IsimplifiedUser>(
        urlNoStateFilter,
        SWR_CONFIG.FORMS
    );
    const onChange = (val: any, action: any) => {
        if (!onChangeFunction) return "";

        if (action.action === "clear") onChangeFunction("", "");
        else onChangeFunction(val.value, val.label);
    };

    const toLabelValue = () => {
        const options = addRoundRobin ? [{ value: 'round robin', label: 'Round Robin' }] : [];
        if (!userListRes) return [];
        for (const u of userListRes?.user) {
            const option = { value: authO ? (u as any).authO : u.id, label: u.name, profile_image: u.profile_image };
            if (!(disabledOptions && disabledOptions.includes(option.value))) {
                options.push(option);
            }
        }
        return options;
    };

    const getValue = () => {
        if (addRoundRobin && value === 'round robin') return { value: 'round robin', label: 'Round Robin' };
        if (!userListRes) return "";
        const user = userListRes?.user.find((u) =>
            authO ? (u as any).authO === value : u.id === value
        );
        if (user) return { label: user.name, value: value, profile_image: user.profile_image };

        // The selected user is not in the filtered list (e.g. assigned before states_licensed_in
        // was introduced). Fall back to the unfiltered list just to show the display name.
        const fallbackUser = userListResNoFilter?.user.find((u) =>
            authO ? (u as any).authO === value : u.id === value
        );
        if (!fallbackUser) return "";
        return { label: fallbackUser.name, value: value, profile_image: fallbackUser.profile_image };
    };

    return (
        <div className={`w-full ${outerClass && outerClass}`}>
            <CustomSelect
                title={title}
                placeholder={placeholder}
                name={name}
                onChangeFunction={onChange}
                className={className ? `flex flex-col w-full ${className}` : "flex flex-col mb-4"}
                titleClassName={titleClassname ? titleClassname : "text-r-medium-small-2"}
                editing={editing}
                value={getValue()}
                options={toLabelValue()}
                containerClassname=""
                isObject
                selectStyles={selectStyles}
                menuPlacement={menuPlacement}
                isClearable={isClearable}
                loading={isLoading}
                showUserImage={showUserImage}
            >
                {children}
            </CustomSelect>
        </div>
    );
};
